import { useEffect, useRef, useState } from 'react';
import Avatar from './Avatar.jsx';
import { sinceLabel } from '../lib/format.js';


/**
 * Things addressed to you that are waiting on you.
 *
 * They were scattered: connection requests were buried inside the Friends tab
 * of the rail, and unread messages were a bare number on your own avatar with
 * no way to see what they were. One bell, one list, everything actionable
 * where it is listed.
 */
export default function Notifications({
  me, requests, guestRequests, companionRequests = [], accepted = [],
  conversations, unread, actors,
  onRespond, onRespondCompanion, onOpenChat, onOpenActor, onDismissAccepted, onSignIn,
}) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => { if (!boxRef.current?.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Unread threads, newest first, with enough of the message to recognise it.
  const threads = conversations
    .map((conversation) => ({
      conversation,
      count: unread[conversation.peer.id] ?? 0,
      last: conversation.messages[conversation.messages.length - 1] ?? null,
    }))
    .filter((t) => t.count > 0 && t.last && t.last.fromId !== me?.id)
    .sort((a, b) => (b.last?.createdAt ?? 0) - (a.last?.createdAt ?? 0));

  // Someone who wrote while you were away has no open window yet.
  const known = new Set(conversations.map((c) => c.peer.id));
  const strays = Object.entries(unread)
    .filter(([actorId, n]) => n > 0 && !known.has(actorId))
    .map(([actorId, n]) => ({ actorId, n, actor: actors.find((a) => a.id === actorId) ?? null }));

  const waiting = me ? requests : guestRequests;
  const tags = me ? companionRequests : [];
  const yeses = me ? accepted : [];
  const total = waiting.length + tags.length + yeses.length + threads.length + strays.length;

  return (
    <div className="notifs" ref={boxRef}>
      <button
        type="button"
        className={`btn notifs-bell ${total > 0 ? 'has-new' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={total > 0 ? `Notifications, ${total} waiting` : 'Notifications'}
        onClick={() => setOpen((v) => !v)}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
          strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M18 9a6 6 0 1 0-12 0c0 5-2 6-2 6h16s-2-1-2-6" />
          <path d="M10.5 20a2 2 0 0 0 3 0" />
        </svg>
        {total > 0 && <span className="badge">{total}</span>}
      </button>

      {open && (
        <div className="notifs-panel" role="dialog" aria-label="Notifications">
          <div className="notifs-head">Notifications</div>

          <div className="notifs-list">
            {total === 0 && <p className="empty-note">Nothing waiting on you.</p>}

            {yeses.map((item) => (
              <div key={`ok-${item.id}`} className="notif">
                <Avatar person={item.who} size="sm" />
                <div className="notif-main">
                  <div className="notif-line">
                    <b>{item.who.displayName}</b> accepted your connection
                    {item.topic ? <> through <em>{item.topic}</em></> : null}
                  </div>
                  <div className="notif-actions">
                    <button
                      type="button" className="btn btn-sm btn-primary"
                      onClick={() => {
                        onDismissAccepted?.(item.id);
                        setOpen(false);
                        onOpenActor?.(item.who);
                      }}
                    >
                      Say hello
                    </button>
                    <button type="button" className="btn btn-sm"
                      onClick={() => onDismissAccepted?.(item.id)}>
                      Dismiss
                    </button>
                  </div>
                </div>
                <span className="notif-when">{sinceLabel(item.at)}</span>
              </div>
            ))}

            {/* Being named in someone's doing places you somewhere, so it is
                a request until you say otherwise — and it waits here with
                everything else that is waiting on you. */}
            {tags.map((request) => (
              <div key={request.doing.id} className="notif">
                <Avatar person={request.from} size="sm" />
                <div className="notif-main">
                  <div className="notif-line">
                    <b>{request.from.displayName}</b> says you were
                    {' '}<em>{request.doing.tag}</em>
                    {request.doing.placeName ? <> at <em>{request.doing.placeName}</em></> : null}
                  </div>
                  <div className="notif-actions">
                    <button type="button" className="btn btn-sm btn-primary"
                      onClick={() => onRespondCompanion(request.doing.id, true)}>Confirm</button>
                    <button type="button" className="btn btn-sm"
                      onClick={() => onRespondCompanion(request.doing.id, false)}>Not with me</button>
                  </div>
                </div>
              </div>
            ))}

            {/* Answerable where they are listed, rather than somewhere else. */}
            {waiting.map((request) => (
              <div key={request.id} className="notif">
                <Avatar person={request.from} size="sm" />
                <div className="notif-main">
                  <div className="notif-line">
                    <b>{request.from.displayName}</b> wants to connect
                    {request.topic ? <> through <em>{request.topic}</em></> : null}
                  </div>
                  {request.note && <div className="notif-note">{request.note}</div>}
                  <div className="notif-actions">
                    {me ? (
                      <>
                        <button type="button" className="btn btn-sm btn-primary"
                          onClick={() => onRespond(request.id, true)}>Accept</button>
                        <button type="button" className="btn btn-sm"
                          onClick={() => onRespond(request.id, false)}>Decline</button>
                      </>
                    ) : (
                      <button type="button" className="btn btn-sm btn-primary" onClick={onSignIn}>
                        Sign up to accept
                      </button>
                    )}
                  </div>
                </div>
                <span className="notif-when">{sinceLabel(request.at)}</span>
              </div>
            ))}

            {threads.map(({ conversation, count, last }) => (
              <button
                key={conversation.key}
                type="button"
                className="notif as-button"
                onClick={() => { setOpen(false); onOpenChat(conversation); }}
              >
                <Avatar person={conversation.peer} size="sm" online={actors.some((a) => a.id === conversation.peer.id && !a.away)} />
                <div className="notif-main">
                  <div className="notif-line">
                    <b>{conversation.peer.displayName}</b>
                    {conversation.postId ? ' replied about your post' : ' messaged you'}
                    {count > 1 ? ` · ${count}` : ''}
                  </div>
                  <div className="notif-note">{last.body}</div>
                </div>
                <span className="notif-when">{sinceLabel(last.createdAt)}</span>
              </button>
            ))}

            {strays.map(({ actorId, n, actor }) => (
              <div key={actorId} className="notif">
                <Avatar person={actor ?? { displayName: 'Someone', seed: actorId }} size="sm" />
                <div className="notif-main">
                  <div className="notif-line">
                    <b>{actor?.displayName ?? 'Someone'}</b> messaged you
                    {n > 1 ? ` · ${n}` : ''}
                  </div>
                  <div className="notif-note">Open their card on the map to read it.</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
